import React, { useEffect, useRef } from "react";
import { Megaphone } from "@phosphor-icons/react";

/**
 * Ad slot — provider-agnostic, swappable via REACT_APP_AD_PROVIDER env var.
 *
 * Supported providers (set REACT_APP_AD_PROVIDER in /app/frontend/.env):
 *   - (empty / undefined / "none")  → render the dark placeholder (default)
 *   - "adsense"                     → Google AdSense responsive ad unit
 *                                     Needs REACT_APP_ADSENSE_CLIENT="ca-pub-XXXX"
 *                                     and (optionally) REACT_APP_ADSENSE_SLOT="1234567"
 *   - "ezoic"                       → Ezoic placeholder div (uses placeholder id 100-110)
 *   - "carbon"                      → Carbon Ads serve script
 *                                     Needs REACT_APP_CARBON_SERVE="CKYI4KQI"
 *                                     and REACT_APP_CARBON_PLACEMENT="pitchiqwin"
 *
 * The script tag for the provider is loaded once at the document-head level by
 * `AdProviderBoot` (mounted near the App root) — see useAdProvider() below.
 *
 * Slot sizes are guidance — the slot is responsive.
 *   - leaderboard: 728x90 (top banner)
 *   - rectangle:   300x250 (sidebar / inline)
 *   - mobile:      320x50  (mobile sticky)
 */

const SIZES = {
  leaderboard: "h-20 sm:h-24",
  rectangle: "h-40 sm:h-48",
  mobile: "h-14",
};

const PROVIDER = (process.env.REACT_APP_AD_PROVIDER || "none").toLowerCase();
const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "";
const ADSENSE_SLOT = process.env.REACT_APP_ADSENSE_SLOT || "";
const CARBON_SERVE = process.env.REACT_APP_CARBON_SERVE || "";
const CARBON_PLACEMENT = process.env.REACT_APP_CARBON_PLACEMENT || "";

/** Load the provider's main script once into <head>. Safe to call on every mount. */
const ensureProviderScript = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("pitchiq-ad-loader")) return;

  let script = null;
  if (PROVIDER === "adsense" && ADSENSE_CLIENT) {
    script = document.createElement("script");
    script.id = "pitchiq-ad-loader";
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    script.crossOrigin = "anonymous";
  } else if (PROVIDER === "ezoic") {
    script = document.createElement("script");
    script.id = "pitchiq-ad-loader";
    script.async = true;
    script.src = "//www.ezojs.com/ezoic/sa.min.js";
  }
  // Carbon loads its own per-slot — no head script needed.
  if (script) document.head.appendChild(script);
};

const AdSensePlaceholder = ({ slotId }) => {
  const ref = useRef(null);
  useEffect(() => {
    ensureProviderScript();
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      /* AdSense push errors are non-fatal */
    }
  }, [slotId]);
  return (
    <ins
      ref={ref}
      className="adsbygoogle"
      style={{ display: "block", width: "100%", height: "100%" }}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={ADSENSE_SLOT || slotId}
      data-ad-format="auto"
      data-full-width-responsive="true"
    />
  );
};

const EzoicPlaceholder = ({ slotId }) => {
  useEffect(() => { ensureProviderScript(); }, []);
  // Ezoic uses numeric placeholder IDs 100-110 — fall back to a hash of slot
  const phid = 100 + (Math.abs(hashCode(slotId)) % 11);
  return <div id={`ezoic-pub-ad-placeholder-${phid}`} style={{ width: "100%", height: "100%" }} />;
};

const CarbonPlaceholder = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !CARBON_SERVE) return;
    if (ref.current.querySelector("script")) return;
    const s = document.createElement("script");
    s.async = true;
    s.id = "_carbonads_js";
    s.src = `//cdn.carbonads.com/carbon.js?serve=${CARBON_SERVE}&placement=${CARBON_PLACEMENT}`;
    ref.current.appendChild(s);
  }, []);
  return <div ref={ref} style={{ width: "100%", height: "100%" }} />;
};

const hashCode = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return h;
};

const Placeholder = () => (
  <>
    <span className="absolute top-1 right-2 text-[8px] uppercase tracking-[0.2em] text-white/30 font-bold">Ad</span>
    <div className="flex items-center gap-3 text-white/30 text-xs uppercase tracking-[0.25em]">
      <Megaphone size={16} weight="duotone" />
      <span>Your ad here · contact info@pitchiq.win</span>
    </div>
  </>
);

export const AdSlot = ({ size = "leaderboard", id = "ad", className = "" }) => {
  const slotId = `pitchiq-${id}`;
  let body;
  if (PROVIDER === "adsense" && ADSENSE_CLIENT) {
    body = <AdSensePlaceholder slotId={slotId} />;
  } else if (PROVIDER === "ezoic") {
    body = <EzoicPlaceholder slotId={slotId} />;
  } else if (PROVIDER === "carbon" && CARBON_SERVE) {
    body = <CarbonPlaceholder />;
  } else {
    body = <Placeholder />;
  }
  return (
    <div
      data-testid={`ad-slot-${id}`}
      data-ad-provider={PROVIDER}
      aria-label="Advertisement"
      className={`relative w-full border border-white/10 bg-[#0E1014] flex items-center justify-center overflow-hidden ${SIZES[size] || SIZES.leaderboard} ${className}`}
    >
      {body}
    </div>
  );
};

export default AdSlot;
